/**
 * World leagues to art pixels, through the one ruled scale.
 *
 * The voyage layer defines a league as a twenty-fourth of a screen width and derives metres per
 * league from the published world scale (docs/RESOLUTION-AND-WORLD-SCALE.md section 4.5, package
 * A at grain 2: 7.80 px per metre across, 6.75 up). This file multiplies the two and asserts, in
 * scale.test.ts, that one frame width of leagues is exactly 960 px and one frame height exactly
 * 540, so a league drawn here is the same league the boat sails. tools/instruments/stature.mjs
 * sizes islands from the same two px-per-metre figures.
 */

import {
  FRAME_HEIGHT_LEAGUES,
  FRAME_WIDTH_LEAGUES,
  METRES_PER_LEAGUE,
  PUBLISHED_WORLD_SCALE,
} from '../voyage/index';

/** The authoring grid, CONTRACT 1.5e: author 960x540, deliver 1920x1080, grain 2. */
export const ART_W = 960;
export const ART_H = 540;
export const GRAIN = 2;
export const DELIVER_W = ART_W * GRAIN;
export const DELIVER_H = ART_H * GRAIN;

/** MODELLED upstream (package A): px per league across and up the screen. 40.0 and 34.6. */
export const PX_PER_LEAGUE_ACROSS = PUBLISHED_WORLD_SCALE.pxPerMetreAcross * METRES_PER_LEAGUE;
export const PX_PER_LEAGUE_UP = PUBLISHED_WORLD_SCALE.pxPerMetreUp * METRES_PER_LEAGUE;

export const FRAME_LEAGUES = { w: FRAME_WIDTH_LEAGUES, h: FRAME_HEIGHT_LEAGUES } as const;

export interface Px {
  readonly x: number;
  readonly y: number;
}

/** World leagues (+y north) to world art pixels (+y down-screen). Not yet camera-relative. */
export function leaguesToWorldPx(p: { readonly x: number; readonly y: number }): Px {
  return { x: p.x * PX_PER_LEAGUE_ACROSS, y: -p.y * PX_PER_LEAGUE_UP };
}

/** World art pixels to screen art pixels, with the camera (in world px) at the frame's centre. */
export function worldPxToScreen(wp: Px, camPx: Px): Px {
  return { x: wp.x - camPx.x + ART_W / 2, y: wp.y - camPx.y + ART_H / 2 };
}

// =============================================================================================
// FITTING THE PICTURE TO THE BOX IT WAS GIVEN
// =============================================================================================
//
// THE DEFECT THIS REPLACES, measured on the built artefact at a63ec92. The delivered size was
// chosen from `window.screen.width * devicePixelRatio` and from nothing else, so it described
// the MONITOR and never the WINDOW. On a 2560-wide monitor a 960x540 window was handed a
// 1920x1080 canvas: 25.0% of the picture was inside the window, 975 px of it hung off the right
// edge, and `overflow: hidden` on the host meant there was not even a scrollbar to reach the
// rest. No boat, no chart, no way to know either existed.
//
// TWO SEPARATE QUESTIONS, and the old code conflated them into one comparison.
//
//   1. HOW BIG, in device pixels, may one art pixel be? Answer: the largest WHOLE NUMBER that
//      still fits inside the box. Nearest-neighbour at a fractional factor is what CONTRACT
//      1.5e means by "non-integer upscaling destroys pixel art" - a 1.5x blit gives some art
//      pixels two device rows and some one, and a 5x7 face with one-pixel strokes comes apart.
//      This is the PURITY question and its answer is an integer, always.
//
//   2. IS THE BOX BIG ENOUGH TO READ? That is a question about CSS pixels, which are a physical
//      size (nominally 1/96 inch), not about device pixels, which are not. It has to be asked
//      separately or two phones of the same physical size answer it differently purely because
//      one has a denser panel - which is exactly what a device-pixel-only rule does.
//
// THE FLOOR, and it is the only judgement in this file. The box must hold the authoring grid at
// one CSS pixel per art pixel: 960 x 540 CSS px. Below that the game does not draw. The number
// is not taste - it is half the apparent pixel CONTRACT 1.5e rules ("apparent pixel: 2 screen
// px"), and it is the point where the type's one-pixel strokes stop having a CSS pixel each.
// MEASURED consequence, and it is the whole argument about phones: the type is a 5x7 face
// (glyphs.ts CAP_H 7, x-height 5) laid out on a grid 960 art px wide, so a box narrower than
// 960 CSS px cannot put a whole CSS pixel behind a stroke no matter what integer is chosen. The
// widest phone viewport in common use is about 932 CSS px in LANDSCAPE and about 430 in
// portrait. Both are under the floor. There is therefore no phone grain that makes this text
// legible, and the honest behaviour is to say so - which is what world.html's notice does.

/** The smallest box, in CSS px, the picture will draw into: the authoring grid at 1:1. */
export const MIN_BOX_CSS_W = ART_W;
export const MIN_BOX_CSS_H = ART_H;

/** What the page actually has: the host element's content box, and the display's ratio. */
export interface AvailableBox {
  /** Host element content-box width in CSS px. */
  readonly cssW: number;
  /** Host element content-box height in CSS px. */
  readonly cssH: number;
  /** `window.devicePixelRatio`. Anything not finite and positive is read as 1. */
  readonly dpr: number;
}

export interface BoxFit {
  /**
   * DEVICE pixels per art pixel: a non-negative integer, and the backing store's scale.
   * Zero means the box cannot hold one device pixel per art pixel, which is a box under
   * 960 x 540 DEVICE px. Callers still have to render something, so they clamp to 1 and hide
   * it; `playable` is what they branch on, never this.
   */
  readonly grain: number;
  /** Backing store, device px. `ART_W * grain` by `ART_H * grain`. */
  readonly deviceW: number;
  readonly deviceH: number;
  /** Laid-out size, CSS px. Backing divided by dpr, so one art pixel is `grain` device px. */
  readonly cssW: number;
  readonly cssH: number;
  /** CSS px per art pixel. CONTRACT 1.5e's ruled apparent pixel is 2; the floor here is 1. */
  readonly apparent: number;
  /** The dpr this fit was computed against, after the not-finite-and-positive guard. */
  readonly dpr: number;
  /** False when the box is under 960 x 540 CSS px, or under one device px per art px. */
  readonly playable: boolean;
}

/**
 * The one place the delivered size is decided. A pure function of the box, so it is fenced by a
 * table in fit.test.ts rather than by looking at a browser, and so nothing has to run to know
 * what a given window will get.
 *
 * It reads the ELEMENT, not the screen. `window.screen` describes hardware the page may have no
 * claim on; `host.clientWidth` describes the room the page was actually given.
 */
export function fitToBox(box: AvailableBox): BoxFit {
  const dpr = Number.isFinite(box.dpr) && box.dpr > 0 ? box.dpr : 1;
  const cssW = Number.isFinite(box.cssW) && box.cssW > 0 ? box.cssW : 0;
  const cssH = Number.isFinite(box.cssH) && box.cssH > 0 ? box.cssH : 0;
  // The largest whole number of device pixels per art pixel that still fits both ways.
  const grain = Math.max(0, Math.floor(Math.min((cssW * dpr) / ART_W, (cssH * dpr) / ART_H)));
  const deviceW = ART_W * grain;
  const deviceH = ART_H * grain;
  return {
    grain,
    deviceW,
    deviceH,
    cssW: deviceW / dpr,
    cssH: deviceH / dpr,
    apparent: grain / dpr,
    dpr,
    playable: grain >= 1 && cssW >= MIN_BOX_CSS_W && cssH >= MIN_BOX_CSS_H,
  };
}
